import { isInvoiceAmountFilterValid } from "@/domain/invoices/invoiceFilterState";
import type { PayLinkRow } from "@/domain/payLinks/payLinkRow";
import { isoDateToUnixEnd, isoDateToUnixStart } from "@/utils/format/isoDate";

function parseMajor(raw: string): number | null {
  const t = raw.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

export interface PayLinkSearchParams {
  customerIds?: string[];
  statuses?: string[];
  dateFrom?: string;
  dateTo?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
  amountMin?: string;
  amountMax?: string;
  tableSearch?: string;
}

export function filterPayLinks(
  rows: PayLinkRow[],
  params: PayLinkSearchParams,
): PayLinkRow[] {
  const {
    customerIds = [],
    statuses = [],
    dateFrom = "",
    dateTo = "",
    dueDateFrom = "",
    dueDateTo = "",
    amountMin = "",
    amountMax = "",
    tableSearch = "",
  } = params;

  let list = [...rows];

  if (customerIds.length) {
    const set = new Set(customerIds);
    list = list.filter((r) => set.has(r.customer_id));
  }
  if (statuses.length) {
    const set = new Set(statuses);
    list = list.filter((r) => set.has(r.status));
  }

  const from = isoDateToUnixStart(dateFrom);
  const to = isoDateToUnixEnd(dateTo);
  if (from !== undefined) list = list.filter((r) => r.created >= from);
  if (to !== undefined) list = list.filter((r) => r.created <= to);

  const dueFrom = isoDateToUnixStart(dueDateFrom);
  const dueTo = isoDateToUnixEnd(dueDateTo);
  if (dueFrom !== undefined || dueTo !== undefined) {
    list = list.filter((row) => {
      const due = row.due_date;
      if (due == null) return false;
      if (dueFrom !== undefined && due < dueFrom) return false;
      if (dueTo !== undefined && due > dueTo) return false;
      return true;
    });
  }

  const min = isInvoiceAmountFilterValid(amountMin, amountMax)
    ? parseMajor(amountMin)
    : null;
  const max = isInvoiceAmountFilterValid(amountMin, amountMax)
    ? parseMajor(amountMax)
    : null;
  if (min !== null || max !== null) {
    list = list.filter((row) => {
      const major = row.amount / 100;
      if (min !== null && major < min) return false;
      if (max !== null && major > max) return false;
      return true;
    });
  }

  const q = tableSearch.trim().toLowerCase();
  if (q) {
    list = list.filter(
      (r) =>
        r.invoice_number.toLowerCase().includes(q) ||
        r.hosted_url.toLowerCase().includes(q) ||
        r.invoice_id.toLowerCase().includes(q),
    );
  }

  list.sort((a, b) => b.created - a.created);
  return list;
}

export function payLinksWithUrl(rows: PayLinkRow[]): PayLinkRow[] {
  return rows.filter((r) => Boolean(r.hosted_url?.trim()));
}
